import styles from '@sicredi/styles/_select.scss';
import cn from 'clsx';
import { UseComboboxState, useCombobox } from 'downshift';
import React, { useEffect, useMemo, useRef, useState } from 'react';

import { Button } from '../button';
import { Input } from '../input';

import { Icon } from '../icon';
import { Highlighter } from '../shared/highlighter';
import { getFocusableElement } from '../utils';
import { Item, Props as SelectProps } from './Select';

export type Value = Item | Item[];

const Multiple: React.FC<SelectProps> = ({
  data,
  name,
  label,
  value,
  onBlur,
  onFocus, // TODO: tratar evento de onFocus
  onChange,
  className,
  inputProps,
  multipleAlwaysCount,
  ...props
}) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const toggleButtonRef = useRef<HTMLButtonElement>(null);
  const internalInputRef = useRef<HTMLInputElement>(null);
  const externalFocusableEl = useRef<HTMLElement | null>(null);
  const shouldFocusExternalEl = useRef<Boolean>(false);
  const [inputItems, setInputItems] = useState(data);
  const [selectedItems, setSelectedItems] = useState<Item[]>([]);
  const internalPreviousActive = useRef<HTMLElement | null>(null);

  function handleChangeSelectedItems(nextSelectedItems: Item[]) {
    !value && setSelectedItems(nextSelectedItems);

    onChange && onChange(nextSelectedItems.map(({ value }) => value));
  }

  const {
    isOpen,
    closeMenu,
    inputValue,
    getMenuProps,
    getItemProps,
    getInputProps,
    getLabelProps,
    setInputValue,
    highlightedIndex,
    getComboboxProps,
    getToggleButtonProps,
  } = useCombobox({
    items: inputItems,
    selectedItem: null,
    onInputValueChange({ inputValue }) {
      setInputItems(
        data.filter(
          item =>
            item.label &&
            String(item.label)
              .toLowerCase()
              .includes((inputValue || '').toLowerCase())
        )
      );
    },
    onSelectedItemChange({ selectedItem }) {
      if (!selectedItem) {
        return;
      }

      const index = selectedItems.findIndex(
        item => selectedItem.value === item.value
      );

      const nextSelectedItems =
        index === -1
          ? [...selectedItems, selectedItem]
          : selectedItems.filter((_, i) => index !== i);

      handleChangeSelectedItems(nextSelectedItems);
    },
    stateReducer(state, actionAndChanges) {
      const { changes, type } = actionAndChanges;

      let nextState: Partial<UseComboboxState<Item>> = state;

      switch (type) {
        case useCombobox.stateChangeTypes.ToggleButtonClick: {
          nextState = {
            ...changes,
            inputValue: '',
          };
          break;
        }
        case useCombobox.stateChangeTypes.InputBlur: {
          nextState = {
            ...changes,
            inputValue: state.isOpen ? state.inputValue : '',
          };
          break;
        }
        case useCombobox.stateChangeTypes.InputKeyDownEscape: {
          nextState = {
            ...state,
            isOpen: false,
          };
          break;
        }
        case useCombobox.stateChangeTypes.ItemClick:
        case useCombobox.stateChangeTypes.InputKeyDownEnter: {
          nextState = {
            ...state,
            selectedItem: changes.selectedItem,
          };
          break;
        }
        case useCombobox.stateChangeTypes.ControlledPropUpdatedSelectedItem: {
          nextState = state;
          break;
        }
        default: {
          nextState = changes;
          break;
        }
      }

      const shouldFocus = !state.isOpen && nextState.isOpen;
      const shouldBlur = state.isOpen && !nextState.isOpen;

      if (shouldFocus) {
        execNativeMethod(internalInputRef.current, 'focus');
      } else if (shouldBlur) {
        execNativeMethod(toggleButtonRef.current, 'focus');
        execNativeMethod(toggleButtonRef.current, 'blur');
      }

      return nextState;
    },
  });

  useEffect(() => {
    setInputItems(data);
  }, [data]);

  useEffect(() => {
    if (value && Array.isArray(value)) {
      const defaultValues = value
        .map(itemValue => data.find(item => item.value === itemValue))
        .filter(Boolean) as Item[];

      setSelectedItems(defaultValues);
    } else {
      setSelectedItems([]);
    }
  }, [value, data]);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      const isTab = e.keyCode === 9;
      const isShift = e.shiftKey;

      const focusPrevious = isShift && isTab;
      const focusNext = !isShift && isTab;

      if (focusPrevious) {
        shouldFocusExternalEl.current = true;
      } else if (focusNext) {
        externalFocusableEl.current = getFocusableElement({
          direction: 'next',
        });
        shouldFocusExternalEl.current = true;
      }
    };

    const handleFocusIn = (e: FocusEvent) => {
      if (!wrapperRef.current) return;

      const focusedEl = e.relatedTarget as HTMLElement;
      const isInternalEl = wrapperRef.current.contains(focusedEl);

      if (isInternalEl) return;

      externalFocusableEl.current = focusedEl;
    };

    document.addEventListener('keydown', handleKeyDown);
    document.addEventListener('focusin', handleFocusIn);

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.removeEventListener('focusin', handleFocusIn);
    };
  }, []);

  const inputLabel = useMemo(() => {
    const selected = selectedItems.length;

    return !multipleAlwaysCount && selected === data.length && selected !== 0
      ? 'Todos selecionados'
      : selected > 1
      ? `${selected} itens selecionados`
      : selected
      ? selectedItems[0].label
      : '';
  }, [selectedItems, data]);

  function execNativeMethod(el: HTMLElement | null, method: 'focus' | 'blur') {
    requestAnimationFrame(() => el && el[method]());
  }

  function selectAllItems() {
    handleChangeSelectedItems(data);
    closeMenu();
  }

  function unselectAllItems() {
    handleChangeSelectedItems([]);
    setInputValue('');
    execNativeMethod(internalInputRef.current, 'focus');
  }

  function handleActionsBlur() {
    requestAnimationFrame(() => {
      if (
        document.activeElement!.getAttribute('data-multiple-select-focus') ===
        null
      ) {
        closeMenu();
      }
    });
  }

  function handleInputFocus(event: React.FocusEvent<HTMLInputElement>) {
    if (isOpen) return;

    internalPreviousActive.current = event.currentTarget;

    execNativeMethod(inputRef.current, 'blur');
  }

  function handleInputBlur(event: React.FocusEvent<HTMLInputElement>) {
    if (shouldFocusExternalEl.current) {
      shouldFocusExternalEl.current = false;

      execNativeMethod(externalFocusableEl.current, 'focus');
    }

    onBlur && onBlur(event);
  }

  function handleToggleButtonBlur(event: React.FocusEvent<HTMLButtonElement>) {
    if (isOpen) return;

    if (internalPreviousActive.current === inputRef.current) {
      internalPreviousActive.current = null;
    } else {
      internalPreviousActive.current = event.currentTarget;

      execNativeMethod(inputRef.current, 'focus');
    }
  }

  const SelectItems = React.useMemo(() => {
    if (isOpen) {
      if (inputItems.length === 0) {
        return (
          <li style={{ pointerEvents: 'none' }}>
            Não foi possível encontrar resultados para{' '}
            <span className={styles['not-found-text']} >{inputValue}</span>
          </li>
        );
      }

      return inputItems.map((option, index) => (
        <li
          className={cn(styles['item'], className, {
            [styles['-highlighted']]: highlightedIndex === index,
            [styles['-selected']]: selectedItems.some(
              item => item.value === option.value
            ),
          })}
          aria-selected={selectedItems.includes(option) ? 'true' : 'false'}
          key={String(option.value)}
          data-testid={
            props['data-testid'] ? `${props['data-testid']}-item-${index}` : undefined
          }
          {...getItemProps({
            index,
            item: option,
          })}
        >
          <Highlighter text={inputValue}>{option.label}</Highlighter>
        </li>
      ));
    }

    return null;
  }, [
    isOpen,
    inputItems,
    inputValue,
    selectedItems,
    className,
    highlightedIndex,
  ]);

  return (
    <div
      className={cn(styles['sicredi-select'], className, {
        [styles['-multiple']]: true,
      })}
      {...getComboboxProps({
        ref: wrapperRef,
      })}
    >
      <button
        type="button"
        className={cn(styles['selections'], className)}
        data-testid={
          props['data-testid'] ? `${props['data-testid']}-toggle-button` : undefined
        }
        {...getToggleButtonProps({
          ref: toggleButtonRef,
          onBlur: handleToggleButtonBlur,
          readOnly: props.readOnly,
          disabled: props.disabled || props.readOnly,
          tabIndex: 0,
          'aria-label': `${isOpen ? 'Fechar' : 'Abrir'} caixa de seleção`,
        })}
      >
        <Input
          icon={
            <Icon
              name="chevron-down"
              aria-hidden="true"
              className={cn(styles['icon'], {
                [styles['-invalid']]: !!props.errorMessage && !props.disabled,
              })}
            />
          }
          label={label}
          name={name}
          value={inputLabel}
          readOnly={true}
          ref={inputRef}
          onBlur={handleInputBlur}
          onFocus={handleInputFocus}
          {...props}
        />
      </button>

      <div
        className={styles['dropdown']}
        aria-hidden={isOpen ? 'false' : 'true'}
      >
        <input
          type="text"
          aria-autocomplete="list"
          aria-controls="listbox"
          placeholder="Pesquisar..."
          data-multiple-select-focus=""
          data-testid={
            props['data-testid'] ? `${props['data-testid']}-input` : undefined
          }
          {...getInputProps({
            ...inputProps,
            ref: internalInputRef,
          })}
          onBlur={undefined}
        />

        <label {...getLabelProps()}>Pesquisar</label>

        <ul
          className={styles['listbox']}
          data-testid={
            props['data-testid'] ? `${props['data-testid']}-menu` : undefined
          }
          {...getMenuProps({
            'aria-label': 'Lista de opções de seleção',
          })}
        >
          {SelectItems}
        </ul>
        <div
          className={cn(styles['actions'], className)}
          onKeyUp={getToggleButtonProps().onKeyDown}
        >
          {selectedItems.length < data.length && (
            <div className={cn(styles['action'], className)}>
              <Button
                onClick={selectAllItems}
                onBlur={handleActionsBlur}
                data-multiple-select-focus=""
                data-testid={
                  props['data-testid'] ? `${props['data-testid']}-select-all-button` : undefined
                }
              >
                Selecionar tudo
              </Button>
            </div>
          )}
          {!!selectedItems.length && (
            <div
              className={cn(styles['action'], className, {
                [styles['-right']]: !(selectedItems.length < data.length),
              })}
            >
              <Button
                onBlur={handleActionsBlur}
                data-multiple-select-focus=""
                onClick={unselectAllItems}
                data-testid={
                  props['data-testid'] ? `${props['data-testid']}-clean-button` : undefined
                }
              >
                Limpar tudo
              </Button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Multiple;
